import {
  FieldTypes,
  type InputFields,
} from '../../../components/FormBuilder/components/Form/FormBuilder';
import { type Category } from '../../ManageCMDBDiscoveredApps/data-layer/discoveryConfigByIdTypes';

/**
 * Extended input field interface including a default value.
 */
export interface ExtendedInputFields extends InputFields {
  defaultValue: string;
}

/**
 * Maps the filtered category API response to an array of input fields
 * consumable by the FormBuilder component.
 *
 * Now, each field's default value is taken directly from the attributeValue.
 *
 * @param data - API response containing the category structure
 * @param onInputChange - Callback triggered on input change
 * @returns Array of input fields for rendering in the UI
 */
export const mapFilteredResponseToInputFields = (
  data: Category[],
  onInputChange: InputFields['inputValueStatus']
): ExtendedInputFields[] => {
  const formFields: ExtendedInputFields[] = [];

  // Traverse categories → subcategories → groups → attributes
  data.forEach((category) =>
    category.subcategories.forEach((subcategory) =>
      subcategory.groups.forEach((group) =>
        group.attributes
          .filter((attribute) => attribute.displayOnUI)
          .forEach((attribute) => {
            // Determine the field type (skip unsupported)
            const fieldType = mapFormatToFieldType(attribute.format);

            if (!fieldType) {
              return;
            }

            formFields.push({
              type: fieldType,
              label: attribute.attributeDisplayName,
              pattern: attribute.validationPattern,
              password: attribute.formatDesc.toLowerCase().includes('password'),
              required: attribute.mandatory.toLowerCase() === 'yes',
              guideMe: false,
              inputDescription: '',
              payloadAtrributeName: attribute.answerAttribute,
              attributeName: attribute.attributeName,
              inputValueStatus: onInputChange,
              fieldDisabled: attribute.editable.toLowerCase() !== 'true',
              defaultValue: attribute.attributeValue ?? '',
            });
          })
      )
    )
  );

  return formFields;
};

/**
 * Converts format number from the API to a FormBuilder-supported input type.
 *
 * @param format - Numeric format ID from the API
 * @returns Corresponding FormBuilder FieldType or null if unsupported
 */
export const mapFormatToFieldType = (format: number): FieldTypes | null => {
  switch (format) {
    case 3: // Text input
    case 8: // Password input
      return FieldTypes.TextInputSingleLine;
    default:
      return null;
  }
};
